/**
 * Text-to-Speech Service
 * Utiliza la Web Speech API para leer textos en voz alta
 * Soporta español e inglés seleccionando la voz más adecuada para cada idioma
 */

class TextToSpeechService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.currentUtterance = null;
    this.voices = [];
    this.isSpeaking = false;

    // Cache de voces por idioma
    this._voiceCache = {};

    this.loadVoices();

    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => {
        this.loadVoices();
        this._voiceCache = {}; // limpiar cache al recargar voces
      };
    }
  }

  loadVoices() {
    this.voices = this.synth.getVoices();
  }

  /**
   * Encuentra la mejor voz disponible para un idioma dado
   * @param {'es'|'en'} lang
   */
  _getBestVoice(lang) {
    if (this._voiceCache[lang]) return this._voiceCache[lang];

    const voices = this.synth.getVoices();

    if (lang === 'en') {
      // Preferencia en inglés: voces neurales/premium de Google o Microsoft
      const enPriority = [
        v => v.name.includes('Google US English'),
        v => v.name.includes('Google UK English'),
        v => v.name.toLowerCase().includes('samantha'),   // macOS
        v => v.name.toLowerCase().includes('daniel'),     // macOS
        v => v.name.toLowerCase().includes('zira'),       // Windows
        v => v.name.toLowerCase().includes('david'),      // Windows
        v => v.lang === 'en-US',
        v => v.lang === 'en-GB',
        v => v.lang.startsWith('en'),
      ];
      for (const test of enPriority) {
        const found = voices.find(test);
        if (found) { this._voiceCache[lang] = found; return found; }
      }
    } else {
      // Preferencia en español: voces de Google o Microsoft
      const esPriority = [
        v => v.name.includes('Google español'),
        v => v.name.includes('Google Español'),
        v => v.name.toLowerCase().includes('diego'),      // Windows es-AR
        v => v.name.toLowerCase().includes('paulina'),    // macOS es-MX
        v => v.name.toLowerCase().includes('jorge'),      // Windows es-ES
        v => v.lang === 'es-AR',
        v => v.lang === 'es-MX',
        v => v.lang === 'es-ES',
        v => v.lang.startsWith('es'),
      ];
      for (const test of esPriority) {
        const found = voices.find(test);
        if (found) { this._voiceCache[lang] = found; return found; }
      }
    }

    return voices[0] || null;
  }

  /**
   * Lee un texto en voz alta
   * @param {string} text - El texto a leer
   * @param {Object} options
   * @param {'es'|'en'} options.lang - Idioma del texto (default 'es')
   * @param {number} options.rate  - Velocidad (default 1.0)
   * @param {number} options.pitch - Tono (default 1.3)
   * @param {number} options.volume - Volumen (default 1)
   * @param {Function} options.onEnd
   * @param {Function} options.onStart
   */
  _removeEmojis(text) {
    return text.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
  }

  _normalizeText(text) {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  speak(text, options = {}) {
    this.stop();
    if (!text) return;

    const lang = options.lang || 'es';
    const utteranceLang = lang === 'en' ? 'en-US' : 'es-ES';
    const withoutEmojis = this._removeEmojis(text);
    const processedText = lang === 'es' ? this._normalizeText(withoutEmojis) : withoutEmojis;

    this.currentUtterance = new SpeechSynthesisUtterance(processedText);
    this.currentUtterance.lang = utteranceLang;

    const voice = this._getBestVoice(lang);
    if (voice) {
      this.currentUtterance.voice = voice;
    }

    this.currentUtterance.rate   = options.rate   || 1.0;
    this.currentUtterance.pitch  = options.pitch  || 1.3;
    this.currentUtterance.volume = options.volume || 1;

    this.currentUtterance.onstart = () => {
      this.isSpeaking = true;
      if (options.onStart) options.onStart();
    };

    this.currentUtterance.onend = () => {
      this.isSpeaking = false;
      if (options.onEnd) options.onEnd();
    };

    this.currentUtterance.onerror = (event) => {
      console.error('Error en TTS:', event);
      this.isSpeaking = false;
    };

    this.synth.speak(this.currentUtterance);
  }

  stop() {
    if (this.synth.speaking) this.synth.cancel();
    this.isSpeaking = false;
  }

  pause() {
    if (this.synth.speaking && !this.synth.paused) this.synth.pause();
  }

  resume() {
    if (this.synth.paused) this.synth.resume();
  }

  get speaking() {
    return this.isSpeaking;
  }

  getVoices() {
    return this.voices;
  }

  setVoice(voice) {
    this._voiceCache = {};
    this.selectedVoice = voice;
  }
}

export const ttsService = new TextToSpeechService();
